import { t } from '@lingui/core/macro';
import { useMutation } from '@tanstack/react-query';

import { participantProfilesControllerSendEmploymentEmailVerification } from '@/api/core-api';

/**
 * The server refused because a code was emailed moments ago. Distinct from a
 * genuine failure: the participant does have a usable code in their inbox, so
 * callers can carry on to code entry instead of treating this as an error.
 */
export class WorkEmailCodeThrottledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkEmailCodeThrottledError';
  }
}

export const useSendWorkEmailVerification = () => {
  return useMutation({
    mutationFn: async ({ profileId }: { profileId: string }) => {
      const response =
        await participantProfilesControllerSendEmploymentEmailVerification({
          path: { id: profileId },
        });
      if (response.status === 409) {
        throw new Error(
          t`That work email is already verified on another account.`,
        );
      } else if (response.status === 429) {
        throw new WorkEmailCodeThrottledError(
          t`You've requested a code recently. Check your inbox, or wait a minute to request another.`,
        );
      } else if (response.error) {
        throw new Error(t`Failed to send your verification code.`);
      }
      return response.data;
    },
  });
};
